import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import FitmentTireSizeOption from './components/fitment-tire-size-option/fitment-tire-size-option';
import OptionalSizes from './components/optional-sizes/optional-sizes';
import OptionalSizesHeading from './components/optional-sizes-heading/optional-sizes-heading';
import Button from '../../../../../button/button';
import Collapsible from '../../../../../collapsible/collapsible';
import { EVENTS } from '../../../../fitment-constants';
import FitmentTireSizeHelpGuide from '../../../fitment-tire-size-help-guide/fitment-tire-size-help-guide';
import {
  assemblyPropType,
  modelPropType,
  trimPropType
} from '../../fitment-by-vehicle-util';
import FitmentStepContainer from '../fitment-step-container/fitment-step-container';

import {
  useMediaQuery,
  useScrollTo
} from '../../../../../../shared/hooks/hooks';
import useFitmentSize from '../../../../../../shared/hooks/use-fitment-size/use-fitment-size';

import { isArrayWithLength } from '../../../../../../shared/utils/validators/validators';

import './fitment-tire-size.scss';

function FitmentTireSize(props) {
  const {
    assemblies,
    getOptionalSizes,
    isEditing,
    isYearAfterModel,
    onEvent,
    onShopProducts,
    selectedAssembly,
    selectedCrumb,
    selectedFitment,
    selectedModel,
    selectedRideHeight,
    selectedTrim,
    selectedYear,
    setSelectedAssembly,
    renderShopProductsButton,
    showShopProductsButton = true,
    shouldCallShopProducts,
    textOverrides
  } = props;

  const {
    hasStockRideHeight,
    isLoadingOptionalSizes,
    isOptionalSizesOpen,
    optionalSizes,
    selectAssembly,
    selectOptionalSize,
    shopProducts,
    selectedOptionalSize,
    selectedOptionalSizeGrouping,
    setSelectedOptionalSizeGrouping,
    standardAssemblies,
    staggeredAssemblies,
    setIsOptionalSizesOpen
  } = useFitmentSize({
    assemblies,
    getOptionalSizes,
    isEditing,
    isYearAfterModel,
    onEvent,
    onShopProducts,
    selectedAssembly,
    selectedFitment,
    selectedModel,
    selectedRideHeight,
    selectedTrim,
    selectedYear,
    setSelectedAssembly,
    shouldCallShopProducts
  });
  const scrollToShopProducts = useScrollTo('#fitment-container', {
    offset: 120
  });
  const [shopProductsWrapperStyle, setShopProductsWrapperStyle] = useState('');
  const shopAllProductsButtonParentRef = useRef(null);

  const { isMinMd } = useMediaQuery();

  const optionalSizesToggleLabel =
    !selectedRideHeight || hasStockRideHeight
      ? `see ${isOptionalSizesOpen ? 'fewer' : 'more'} tire sizes`
      : `${selectedRideHeight?.type?.toLowerCase()} tire sizes`;

  const checkIfButtonShouldBeFixed = useCallback(() => {
    const fixedStyleButtonWrapper =
      !isMinMd &&
      shopAllProductsButtonParentRef?.current?.getBoundingClientRect()?.y +
        120 <
        0
        ? 'fixed-button-wrapper'
        : '';
    if (shopProductsWrapperStyle !== fixedStyleButtonWrapper) {
      setShopProductsWrapperStyle(fixedStyleButtonWrapper);
    }
  }, [isMinMd, shopProductsWrapperStyle]);

  useEffect(() => {
    window.addEventListener('scroll', checkIfButtonShouldBeFixed);
    return () =>
      window.removeEventListener('scroll', checkIfButtonShouldBeFixed);
  }, [checkIfButtonShouldBeFixed]);

  return (
    <FitmentStepContainer
      containerRef={shopAllProductsButtonParentRef}
      heading={
        <>
          Select the <strong>original</strong> tire size for your vehicle
        </>
      }
      moreInfoContent={() => <FitmentTireSizeHelpGuide title={selectedCrumb} />}
      onMoreInfoContentClick={() => {
        onEvent?.({
          name: EVENTS.VEHICLE_FITMENT_ASSEMBLY_NEED_HELP
        });
      }}
      styleName="fitment-tire-size"
    >
      <div styleName="oe-sizes-wrapper">
        <div
          aria-label="Select the original tire size for your vehicle"
          role="radiogroup"
          styleName="oe-sizes-container"
        >
          {isArrayWithLength(standardAssemblies) && (
            <div styleName="oe-sizes-option-wrapper">
              {standardAssemblies.map((assembly, i) => (
                <FitmentTireSizeOption
                  assembly={{ ...assembly, isOE: true }}
                  isSelected={selectedOptionalSize ? false : void 0}
                  key={`standard-size-${assembly.description}-${i}`}
                  onClick={selectAssembly}
                  selectedAssembly={selectedAssembly}
                />
              ))}
            </div>
          )}

          {isArrayWithLength(staggeredAssemblies) && (
            <div styleName="oe-sizes-option-wrapper">
              {staggeredAssemblies.map((assembly, i) => (
                <FitmentTireSizeOption
                  assembly={{ ...assembly, isOE: true }}
                  isSelected={selectedOptionalSize ? false : void 0}
                  key={`standard-size-staggered-${assembly.description}-${i}`}
                  onClick={selectAssembly}
                  selectedAssembly={selectedAssembly}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <hr styleName="divider" />

      {(isArrayWithLength(optionalSizes) ||
        !selectedAssembly ||
        isLoadingOptionalSizes) && (
        <>
          <OptionalSizesHeading selectedRideHeight={selectedRideHeight} />
          <Collapsible
            collapseType="SECONDARY"
            isDisabled={!selectedAssembly && !selectedOptionalSize}
            isOpen={isOptionalSizesOpen}
            label={optionalSizesToggleLabel}
            onToggle={setIsOptionalSizesOpen}
            styleName="optional-sizes"
          >
            <OptionalSizes
              isStaggered={selectedFitment?.isStaggered}
              optionalSizes={optionalSizes}
              selectedAssembly={selectedAssembly}
              selectedOptionalSize={selectedOptionalSize}
              selectedOptionalSizeGrouping={selectedOptionalSizeGrouping}
              selectedRideHeight={selectedRideHeight}
              setSelectedOptionalSize={selectOptionalSize}
              setSelectedOptionalSizeGrouping={setSelectedOptionalSizeGrouping}
            />
          </Collapsible>
        </>
      )}

      {showShopProductsButton && (
        <>
          {renderShopProductsButton ? (
            renderShopProductsButton({
              isDisabled: !selectedAssembly,
              isEditing: isEditing,
              onClick: shopProducts
            })
          ) : (
            <div
              styleName={`${shopProductsWrapperStyle} shop-products-wrapper`}
            >
              <Button
                data-testid="fitment-tire-size__shop-products"
                isDisabled={!selectedAssembly}
                isFullWidth
                isPrimary
                onClick={() => {
                  shopProducts();
                  shopProductsWrapperStyle && scrollToShopProducts();
                }}
              >
                {textOverrides?.fitmentContinueCta ?? 'Shop Products'}
              </Button>
            </div>
          )}
        </>
      )}
    </FitmentStepContainer>
  );
}

FitmentTireSize.propTypes = {
  assemblies: PropTypes.arrayOf(assemblyPropType),
  getOptionalSizes: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
  isYearAfterModel: PropTypes.bool,
  onEvent: PropTypes.func,
  onShopProducts: PropTypes.func,
  renderShopProductsButton: PropTypes.func,
  selectedAssembly: assemblyPropType,
  selectedCrumb: PropTypes.string,
  selectedFitment: PropTypes.shape({
    frontTireSize: PropTypes.string,
    isOE: PropTypes.bool,
    isStaggered: PropTypes.bool,
    rearTireSize: PropTypes.string,
    sizeQualifier: PropTypes.string
  }),
  selectedModel: modelPropType,
  selectedRideHeight: PropTypes.object,
  selectedTrim: trimPropType,
  selectedYear: PropTypes.any,
  setSelectedAssembly: PropTypes.func,
  shouldCallShopProducts: PropTypes.bool,
  showShopProductsButton: PropTypes.bool,
  textOverrides: PropTypes.shape({
    fitmentContinueCta: PropTypes.string
  })
};

export default FitmentTireSize;
